/**
 * Tell the stylesheet which kind of scrollbar this machine draws.
 *
 * There are two kinds, and CSS has no query that tells them apart:
 *
 *  - an OVERLAY bar (macOS by default, every phone) floats over the content, takes
 *    no width and hides itself when nothing scrolls;
 *  - a CLASSIC bar (Windows, Linux, macOS set to "always show") takes its width out
 *    of the box it scrolls and stays on screen for as long as the box overflows.
 *
 * The `scroll-soft` rules in `styles.css` restyle the classic bar only. They cannot
 * be written without this mark: a `::-webkit-scrollbar` rule that reaches an overlay
 * bar turns it INTO a classic one, so the fix for Windows would put a permanent bar
 * on every Mac, where there was none.
 *
 * So the kind is measured - a box that is told to scroll is narrower inside than
 * outside by exactly the width of a classic bar, and not at all with an overlay one -
 * and written on `<html>` as `data-scrollbars="classic"`.
 */

/** The width a scrollbar takes out of the box it scrolls; 0 for an overlay bar. */
export function classicScrollbarWidth(doc: Document): number {
  const probe = doc.createElement("div");
  probe.style.cssText =
    "position:absolute;top:-9999px;left:-9999px;width:100px;height:100px;overflow:scroll;visibility:hidden";
  doc.body.appendChild(probe);
  const width = probe.offsetWidth - probe.clientWidth;
  probe.remove();
  return width;
}

/**
 * Measure, and write the answer on `<html>`.
 *
 * It is one forced layout of a 100px box, and it is not cached on purpose: macOS changes
 * the kind while the app is open (a mouse is plugged in, the setting is changed). An app
 * keeps the mark true with `bindScrollbarKind`; a piece that can load on its own, such as
 * the on-page editor, also calls this from an effect.
 */
export function markScrollbarKind(doc: Document | undefined = typeof document === "undefined" ? undefined : document): void {
  if (!doc?.body) return;
  if (classicScrollbarWidth(doc) > 0) doc.documentElement.dataset.scrollbars = "classic";
  else delete doc.documentElement.dataset.scrollbars;
}

/**
 * Keep the mark true for as long as the app is open, so `scroll-soft` works on every page and
 * not only after the builder has been opened. The window gets focus back after the setting
 * was changed, and that is when it is measured again. Returns the way to stop.
 */
export function bindScrollbarKind(win: Window = window): () => void {
  const measure = () => markScrollbarKind(win.document);
  measure();
  win.addEventListener("focus", measure);
  return () => win.removeEventListener("focus", measure);
}
